import { useEffect, useState, useRef } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { 
  Ticket, MessageSquare, ShieldAlert, CheckCircle, Clock, AlertCircle,
  Send, User, Mail, Phone, ExternalLink 
} from "lucide-react";
import { toast } from "sonner";

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyStatus, setReplyStatus] = useState("in_progress");
  const [sendingReply, setSendingReply] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const messageEndRef = useRef(null);

  const loadTickets = async () => {
    try {
      const res = await api.get("/admin/tickets", { params: { status: filterStatus } });
      setTickets(res.data || []);
      
      // Keep active ticket selected if it was open
      if (activeTicket) {
        const updated = res.data.find(t => t.ticket_id === activeTicket.ticket_id);
        if (updated) setActiveTicket(updated);
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [filterStatus]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeTicket]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !activeTicket) return;
    setSendingReply(true);
    try {
      await api.post(`/admin/tickets/${activeTicket.ticket_id}/reply`, {
        text: replyText,
        status: replyStatus
      });
      toast.success("Reply sent successfully!");
      setReplyText("");
      // Reload tickets to refresh chat view
      await loadTickets();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (statusVal) => {
    if (!activeTicket) return;
    try {
      await api.put(`/admin/tickets/${activeTicket.ticket_id}`, { status: statusVal });
      toast.success("Ticket status updated!");
      await loadTickets();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const handleUpdatePriority = async (prioVal) => {
    if (!activeTicket) return;
    try {
      await api.put(`/admin/tickets/${activeTicket.ticket_id}`, { priority: prioVal });
      toast.success("Ticket priority updated!");
      await loadTickets();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const getPriorityBadge = (prio) => {
    const map = {
      high: "bg-red-100 text-red-700 border-red-200",
      medium: "bg-amber-100 text-amber-700 border-amber-200",
      low: "bg-blue-100 text-blue-700 border-blue-200"
    };
    return (
      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase ${map[prio] || map.low}`}>
        {prio}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    const map = {
      open: <AlertCircle className="w-4 h-4 text-amber-500" />,
      in_progress: <Clock className="w-4 h-4 text-blue-500 animate-pulse" />,
      resolved: <CheckCircle className="w-4 h-4 text-emerald-500" />,
      closed: <CheckCircle className="w-4 h-4 text-slate-400" />
    };
    return map[status] || map.open;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-140px)]">
      <PageHeader 
        title="Support Desk" 
        subtitle="Manage and resolve user queries & support tickets" 
      />

      <div className="flex-1 flex gap-5 min-h-0">
        {/* Ticket List Panel */}
        <div className="w-full md:w-5/12 flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Tabs Filter */}
          <div className="border-b border-slate-200 p-3 bg-slate-50/50">
            <Tabs defaultValue="all" onValueChange={setFilterStatus}>
              <TabsList className="grid grid-cols-5 w-full text-xs">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="open">Open</TabsTrigger>
                <TabsTrigger value="in_progress">Active</TabsTrigger>
                <TabsTrigger value="resolved">Done</TabsTrigger>
                <TabsTrigger value="closed">Closed</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* List scroll container */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading ? (
              <div className="text-center py-10 text-xs text-muted-foreground">Loading tickets...</div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12 text-xs text-muted-foreground flex flex-col items-center gap-1.5">
                <Ticket className="w-8 h-8 text-slate-300" />
                No support tickets found
              </div>
            ) : (
              tickets.map((tkt) => (
                <div
                  key={tkt.ticket_id}
                  onClick={() => { setActiveTicket(tkt); setReplyStatus(tkt.status); }}
                  className={`border rounded-xl p-3.5 cursor-pointer transition-all flex items-center justify-between shadow-sm ${
                    activeTicket?.ticket_id === tkt.ticket_id
                      ? "border-primary/80 bg-primary/[0.03]"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="flex items-center gap-1.5">
                      {getStatusIcon(tkt.status)}
                      <span className="font-bold text-xs text-slate-800 truncate">{tkt.subject}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 truncate">{tkt.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[10px] text-slate-400 font-semibold">{tkt.user_name || "User"}</span>
                      <span className="text-[10px] text-slate-300">•</span>
                      <span className="text-[10px] text-slate-400">{new Date(tkt.created_at).toLocaleDateString("en-IN")}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {getPriorityBadge(tkt.priority)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat / Ticket Reply Panel */}
        <div className="hidden md:flex flex-1 flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {activeTicket ? (
            <div className="flex flex-col h-full">
              {/* Header Info */}
              <div className="border-b border-slate-200 p-4 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200/60 flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-slate-800 leading-tight flex items-center gap-1.5">
                      {activeTicket.user_name}
                      <span className="text-[10px] text-slate-400 font-normal">({activeTicket.user_id})</span>
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                      <span>{activeTicket.user_email}</span>
                      {activeTicket.user_phone && (
                        <>
                          <span>•</span>
                          <span>{activeTicket.user_phone}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Priority Select */}
                  <select 
                    value={activeTicket.priority} 
                    onChange={(e) => handleUpdatePriority(e.target.value)}
                    className="border border-slate-300 rounded-lg text-xs p-1.5 bg-white font-medium"
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>

                  {/* Status Select */}
                  <select 
                    value={activeTicket.status} 
                    onChange={(e) => handleUpdateStatus(e.target.value)}
                    className="border border-slate-300 rounded-lg text-xs p-1.5 bg-white font-medium"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/20">
                {/* Initial Description Card */}
                <div className="border border-dashed border-slate-300 rounded-2xl p-4 bg-amber-50/[0.15] max-w-xl mx-auto text-center space-y-1 shadow-sm">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Original Ticket Issue</span>
                  <h4 className="font-bold text-sm text-slate-800 mt-1">{activeTicket.subject}</h4>
                  <p className="text-xs text-slate-600 leading-relaxed mt-1.5">{activeTicket.description}</p>
                </div>

                {/* Reply flow */}
                {(activeTicket.messages || []).map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[70%]">
                      <div className={`rounded-2xl px-4 py-2.5 text-xs leading-relaxed shadow-sm ${
                        msg.sender === "admin"
                          ? "bg-slate-800 text-white rounded-tr-none"
                          : "bg-white border border-slate-200 text-slate-700 rounded-tl-none"
                      }`}>
                        {msg.text}
                      </div>
                      <span className="text-[9px] text-slate-400 mt-1 block px-1 text-right">
                        {msg.sender === "admin" ? "Admin Staff" : "User Client"} • {new Date(msg.created_at).toLocaleString("en-IN", { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
                <div ref={messageEndRef} />
              </div>

              {/* Reply Input Form */}
              <form onSubmit={handleSendReply} className="border-t border-slate-200 p-4 bg-white space-y-3">
                <div className="flex items-center gap-3">
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type reply to user..."
                    className="flex-1 text-xs h-10"
                    disabled={activeTicket.status === "closed"}
                  />
                  <Button 
                    type="submit" 
                    disabled={sendingReply || !replyText.trim() || activeTicket.status === "closed"}
                    className="h-10 px-4 bg-slate-800 text-white hover:bg-slate-900 rounded-xl"
                  >
                    <Send className="w-4 h-4 mr-1.5" />
                    Reply
                  </Button>
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                  <span>Press Send or Enter to submit reply to user client inbox.</span>
                  <div className="flex items-center gap-1.5">
                    <span>Set ticket status to:</span>
                    <select 
                      value={replyStatus} 
                      onChange={(e) => setReplyStatus(e.target.value)}
                      className="border border-slate-300 rounded p-1 bg-white text-xs font-semibold"
                    >
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved / Fixed</option>
                      <option value="closed">Closed / Done</option>
                    </select>
                  </div>
                </div>
              </form>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-slate-400 text-center gap-2">
              <MessageSquare className="w-12 h-12 text-slate-200" />
              <p className="text-sm">Select a ticket from the left panel to review chat history and reply.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
